import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileUp, Loader2, Receipt, Banknote, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { EcheancesMandat } from './EcheancesMandat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const TYPES = { facture: 'Facture', devis: 'Devis', recu: 'Reçu', echeancier: 'Échéancier du mandat' };

const ouvrirGed = async (gedId, headers) => {
  try {
    const r = await axios.get(`${API}/comptabilite/documents/${gedId}/download`, { headers, responseType: 'blob' });
    window.open(URL.createObjectURL(r.data), '_blank');
  } catch { toast.error('Document indisponible'); }
};

const Depot = ({ mandat, headers, onDone }) => {
  const [type, setType] = useState('facture');
  const [montant, setMontant] = useState('');
  const [ref, setRef] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const input = useRef(null);
  const envoyer = async (f) => {
    if (!f) return;
    setEnvoi(true);
    const fd = new FormData();
    fd.append('fichier', f);
    fd.append('type_doc', type);
    fd.append('montant', String(Number(montant) || 0));
    fd.append('reference', ref);
    try {
      const { data } = await axios.post(`${API}/sepa-stripe/admin/mandats/${mandat.id}/documents`, fd, { headers });
      toast.success(`Document classé dans la GED et en comptabilité${data.document?.recu_emis ? ' — reçu de paiement émis' : ''}`);
      setMontant(''); setRef('');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Téléversement impossible'); }
    setEnvoi(false);
    if (input.current) input.current.value = '';
  };
  return (
    <div className="grid grid-cols-2 gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs md:grid-cols-5">
      <select value={type} onChange={(e) => setType(e.target.value)} className="rounded border border-slate-300 px-2 py-1.5" data-testid="mandat-doc-type">
        {Object.entries(TYPES).filter(([k]) => k !== 'echeancier').map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <input type="number" step="0.01" placeholder="Montant € (facultatif)" value={montant} onChange={(e) => setMontant(e.target.value)} className="rounded border border-slate-300 px-2 py-1.5" data-testid="mandat-doc-montant" />
      <input placeholder="N° / référence" value={ref} onChange={(e) => setRef(e.target.value)} className="rounded border border-slate-300 px-2 py-1.5 md:col-span-2" data-testid="mandat-doc-reference" />
      <input ref={input} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx" className="hidden" onChange={(e) => envoyer(e.target.files?.[0])} data-testid="mandat-doc-input" />
      <button type="button" disabled={envoi} onClick={() => input.current?.click()} className="inline-flex items-center justify-center gap-1.5 rounded bg-[#0a3d62] px-3 py-1.5 font-semibold text-white hover:bg-[#0c4a77] disabled:opacity-60" data-testid="mandat-doc-televerser">
        {envoi ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />} Téléverser
      </button>
    </div>
  );
};

const Encaissement = ({ mandat, headers, onDone }) => {
  const [montant, setMontant] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [ref, setRef] = useState('');
  const enregistrer = async () => {
    try {
      const { data } = await axios.post(`${API}/sepa-stripe/admin/mandats/${mandat.id}/encaissements`, { montant: Number(montant), date, reference: ref }, { headers });
      toast.success(`Encaissement enregistré — ${data.recus_emis} reçu(s) émis automatiquement`);
      setMontant(''); setRef('');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Encaissement impossible'); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <Banknote className="h-4 w-4 text-emerald-600" />
      <input type="number" step="0.01" placeholder="Montant encaissé €" value={montant} onChange={(e) => setMontant(e.target.value)} className="w-36 rounded border border-slate-300 px-2 py-1.5" data-testid="mandat-enc-montant" />
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded border border-slate-300 px-2 py-1.5" data-testid="mandat-enc-date" />
      <input placeholder="Référence" value={ref} onChange={(e) => setRef(e.target.value)} className="w-40 rounded border border-slate-300 px-2 py-1.5" data-testid="mandat-enc-reference" />
      <button type="button" disabled={!(Number(montant) > 0)} onClick={enregistrer} className="rounded bg-emerald-600 px-3 py-1.5 font-semibold text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="mandat-enc-enregistrer">Enregistrer l&apos;encaissement</button>
    </div>
  );
};

export const MandatDocuments = ({ mandat, headers, onClose }) => {
  const [data, setData] = useState(null);
  const charger = useCallback(async () => {
    try { setData((await axios.get(`${API}/sepa-stripe/admin/mandats/${mandat.id}/documents`, { headers })).data); } catch { setData({ documents: [], encaissements: [] }); }
  }, [mandat, headers]);
  useEffect(() => { if (mandat) charger(); }, [mandat, charger]);
  return (
    <Dialog open={!!mandat} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mandat-documents-dialog">
        <DialogHeader><DialogTitle className="text-[#0a3d62]">Documents du mandat {mandat?.rum} — {mandat?.membre_nom}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-600">Chaque document est classé dans la GED du membre et en comptabilité. Un reçu de paiement est émis automatiquement dès qu&apos;un montant est réglé, puis réémis à chaque encaissement (« ACQUITTÉ » au solde).</p>
        <Depot mandat={mandat} headers={headers} onDone={charger} />
        <Encaissement mandat={mandat} headers={headers} onDone={charger} />
        {mandat && <EcheancesMandat mandatId={mandat.id} headers={headers} admin />}
        {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {data.encaissements?.length > 0 && <p className="text-xs text-slate-600" data-testid="mandat-enc-total">Encaissé : <b>{eur(data.encaissements.reduce((t, e) => t + e.montant, 0))}</b> sur {eur(data.montant)}</p>}
            {(data.documents || []).length === 0 && <p className="text-sm text-slate-500">Aucun document lié pour l&apos;instant.</p>}
            {(data.documents || []).map((d) => {
              const dernier = (d.recus || [])[d.recus.length - 1];
              return (
                <div key={d.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 p-2.5 text-xs" data-testid={`mandat-doc-${d.id}`}>
                  <FileText className="h-4 w-4 text-[#0a3d62]" />
                  <b>{TYPES[d.type]}</b><span className="truncate text-slate-600">{d.reference || d.nom}</span>
                  {d.montant > 0 && <span className="font-semibold">{eur(d.montant)}</span>}
                  <span className="ml-auto flex items-center gap-1.5">
                    {d.ged_id && <button type="button" onClick={() => ouvrirGed(d.ged_id, headers)} className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-50" data-testid={`mandat-doc-voir-${d.id}`}>Voir</button>}
                    {dernier ? (
                      <button type="button" onClick={() => ouvrirGed(dernier.ged_id, headers)} className={`inline-flex items-center gap-1 rounded px-2 py-1 font-semibold ${dernier.acquitte ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} data-testid={`mandat-doc-recu-${d.id}`}>
                        <Receipt className="h-3.5 w-3.5" /> Reçu V{dernier.version} {dernier.acquitte ? 'ACQUITTÉ' : `— solde ${eur(dernier.reste)}`}
                      </button>
                    ) : d.type !== 'recu' && <span className="text-slate-400">Reçu émis au 1er encaissement</span>}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
